import { useEffect, useState } from 'react';
import { List, ListItem, ListItemText, Typography } from '@material-ui/core';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

type NewsPost = {
  title: string;
  link: string;
  createdAt: string;
  author: string;
};

export const LatestNewsCard = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [posts, setPosts] = useState<NewsPost[]>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('felix');
        const response = await fetch(`${baseUrl}/news`);
        if (!response.ok) {
          throw new Error(
            `Failed to load news: ${response.status} ${response.statusText}`,
          );
        }
        const data = (await response.json()) as { posts: NewsPost[] };
        if (active) {
          setPosts(data.posts);
        }
      } catch (e) {
        if (active) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetch]);

  return (
    <InfoCard title="Latest News">
      {error && <ResponseErrorPanel error={error} />}
      {!error && !posts && <Progress />}
      {posts?.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No blog posts found in Smartbox.
        </Typography>
      )}
      {posts && posts.length > 0 && (
        <List dense disablePadding>
          {posts.map(post => (
            <ListItem key={post.link} disableGutters>
              <ListItemText
                primary={<Link to={post.link}>{post.title}</Link>}
                secondary={[
                  post.author,
                  post.createdAt
                    ? new Date(post.createdAt).toLocaleDateString()
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
    </InfoCard>
  );
};
